import React from 'react'

import CheckBox from '@mui/material/Checkbox'
import type { CheckboxProps } from '@mui/material/Checkbox'

/**
 * A checkbox whose mixed state is expressed the way HTML defines it.
 *
 * MUI's `indeterminate` prop swaps the icon and stamps `aria-checked="mixed"` on
 * the input, but deliberately does NOT set the native `indeterminate` property
 * (there is no HTML attribute for it -- it is settable only from JS). That
 * leaves the input's real state "unchecked" while ARIA claims "mixed", which
 * axe flags as a conformance violation and which leaves the announcement
 * dependent on assistive tech honouring the override.
 *
 * Setting the native property is the host-language mechanism: HTML-AAM maps it
 * to the "mixed" checked state, so screen readers read it off the element
 * itself. A callback ref (rather than an effect) keeps it in sync both when the
 * value changes and when the input remounts.
 */
export const TriStateCheckbox = ({ indeterminate = false, ...props }: CheckboxProps) => {
  const setInputRef = React.useCallback(
    (input: HTMLInputElement | null) => {
      if (input) {
        input.indeterminate = indeterminate
      }
    },
    [indeterminate],
  )

  return <CheckBox {...props} indeterminate={indeterminate} inputRef={setInputRef} />
}
